import { UntypedFormGroup } from '@angular/forms';

export class UtilsHelper {
  public static isNullOrUndefined(value: any): boolean {
    return value === null || value === undefined;
  }

  public static isNumber(val: any): boolean {
    return typeof val === 'number' || val instanceof Number;
  }

  public static isString(val: any): boolean {
    return typeof val === 'string' || val instanceof String;
  }

  public static isEmptyArray(val: any): boolean {
    return Array.isArray(val) && val.length === 0;
  }

  public static isNull(value: any): boolean {
    return value === null;
  }

  public static isUndefined(value: any): boolean {
    return value === undefined;
  }

  public static isObject(value: any): boolean {
    return value !== null && (typeof value === 'object' || typeof value === 'function');
  }

  public static isObjectEmpty(obj: any): boolean {
    return this.isObject(obj) && !Object.values(obj).length;
  }

  public static lastElement<T>(array: T[]): T {
    return array[array.length - 1];
  }

  public static maxElement(array: number[]): number {
    let maxEl: number = array[0];
    array.forEach((el: number) => {
      if (el > maxEl) {
        maxEl = el;
      }
    });
    return maxEl;
  }

  public static maxElementBy<T, U>(array: T[], fieldBy: string, func?: (param: U) => void): T {
    let maxEl: T = array[0];
    array.forEach((el: T) => {
      const compareCurrentValue: number = !!func ? func(el[fieldBy]) : el[fieldBy];
      const compareMaxValue: number = !!func ? func(maxEl[fieldBy]) : maxEl[fieldBy];
      if (compareCurrentValue > compareMaxValue) {
        maxEl = el;
      }
    });
    return maxEl;
  }

  public static minElementBy<T, U>(array: T[], fieldBy: string, func?: (param: U) => void): T {
    let minEl: T = array[0];
    array.forEach((el: T) => {
      const compareCurrentValue: number = !!func ? func(el[fieldBy]) : el[fieldBy];
      const compareMinValue: number = !!func ? func(minEl[fieldBy]) : minEl[fieldBy];
      if (compareCurrentValue < compareMinValue) {
        minEl = el;
      }
    });
    return minEl;
  }

  public static concat<T>(...values: Array<T | ReadonlyArray<T>>): T[] {
    return [].concat(...values);
  }

  public static uniq<T>(array: T[]): T[] {
    return [...new Set(array)];
  }

  public static uniqBy<T>(array: T[], fieldBy: string): T[] {
    const set: Set<any> = new Set();
    const returnArray: T[] = [];
    array.forEach((item: T) => {
      if (!set.has(item[fieldBy])) {
        set.add(item[fieldBy]);
        returnArray.push(item);
      }
    });
    return returnArray;
  }

  // TODO: change pick() lodash after
  public static pick<T extends object, U extends keyof T>(object: T, fields: string[]): U {
    const returnObj: U = Object.create({});
    fields.forEach((key: string) => {
      returnObj[key] = object[key];
    });
    return returnObj;
  }

  // TODO: change find() lodash after
  public static find<T>(object: T[], field: Omit<T, any>): T {
    return object.find((item: T) => {
      return Object.entries(field).every(([key, value]: [string, any]) => value === item[key]) ? item : false;
    });
  }

  public static findLastIndex<T>(arr: T[], key: keyof T, value: T[keyof T]): number {
    for (let i = arr.length - 1; i >= 0; i--) {
      if (arr[i][key] === value) {
        return i;
      }
    }
    return -1;
  }

  public static limitLength(value: string, limit: number, appendDots: boolean = true) {
    return value.length > limit ? `${value.substring(0, limit)}${appendDots ? '...' : ''}` : value;
  }

  public static convertToSnakeCase(camelCaseString: string): string {
    return camelCaseString.replace(/([a-z])([A-Z])/g, '$1_$2').toUpperCase();
  }

  public static getFirstMapKeyWithNonEmptyArray<K, V>(map: Map<K, V[]>): K | undefined {
    for (let [key, value] of map.entries()) {
      if (value.length > 0) {
        return key;
      }
    }
    return undefined;
  }

  public static groupObjectsByField(objects: any[], fieldToGroupBy: string) {
    const groupedObjects = objects.reduce((acc, obj) => {
      // If the group for this id doesn't exist, create it
      if (!acc[obj[fieldToGroupBy]]) {
        acc[obj[fieldToGroupBy]] = [];
      }
      // Push the object into the corresponding group
      acc[obj[fieldToGroupBy]].push(obj);
      return acc;
    }, {} as { [key: number]: any[] });

    return groupedObjects;
  }

  public static formMatchesObject(form: UntypedFormGroup, obj: any): boolean {
    if (!form || !obj) {
      return false;
    }

    const formValues = form.value;

    const filteredFormValues = Object.keys(formValues)
      .filter((key) => key in obj)
      .reduce((acc, key) => {
        acc[key] = formValues[key];
        return acc;
      }, {} as any);

    return Object.keys(filteredFormValues).every((key) => {
      const isMatchingValues = UtilsHelper.deepEqual(filteredFormValues[key], obj[key]);
      return isMatchingValues;
    });
  }

  public static deepEqual(value1: any, value2: any): boolean {
    if (value1 === value2) {
      return true;
    }

    if (value1 == null && value2 == null) {
      return true;
    }

    if (typeof value1 === 'object' && typeof value2 === 'object') {
      const keys1 = Object.keys(value1);
      const keys2 = Object.keys(value2);

      if (keys1.length !== keys2.length) {
        return false;
      }

      return keys1.every((key) => UtilsHelper.deepEqual(value1[key], value2[key]));
    }

    return false;
  }
}
